import { useRef, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Upload } from 'lucide-react';
import { updateBranding, uploadLogo } from '../../api/customers';
import { useLogoUrl } from '../../hooks/useLogoUrl';
import { apiErrorMessage } from '../../lib/api';
import { useToast } from '../../components/ui/Toast';
import { Card, CardBody, CardHeader } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Alert } from '../../components/ui/Alert';
import { BillingSection } from './BillingSection';
import { AdHocContractsSection } from './AdHocContractsSection';
import type { CustomerOrganisation, CustomerSite } from '../../types/api';

const DEFAULT_COLOR = '#2563eb';

function BrandingSection({ org }: { org: CustomerOrganisation }) {
  const queryClient = useQueryClient();
  const { showToast } = useToast();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [color, setColor] = useState(org.brandColor ?? DEFAULT_COLOR);
  const [error, setError] = useState<string | null>(null);

  const logoUrl = useLogoUrl(org.id, org.hasLogo);

  const logoMutation = useMutation({
    mutationFn: (file: File) => uploadLogo(org.id, file),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['customer', org.id] });
      queryClient.invalidateQueries({ queryKey: ['logo', org.id] });
      showToast('Logo updated.');
    },
    onError: (err) => setError(apiErrorMessage(err, 'Could not upload the logo.')),
  });

  const colorMutation = useMutation({
    mutationFn: () => updateBranding(org.id, { brandColor: color }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['customer', org.id] });
      showToast('Brand color updated.');
    },
    onError: (err) => setError(apiErrorMessage(err, 'Could not update the brand color.')),
  });

  function handleFileChange(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    setError(null);
    logoMutation.mutate(file);
    e.target.value = '';
  }

  return (
    <Card>
      <CardHeader>
        <span className="text-sm font-medium text-slate-600">
          Co-branding shown alongside Oakford Technology when this customer's users log in.
        </span>
      </CardHeader>
      <CardBody className="flex flex-col gap-6">
        {error && <Alert tone="error">{error}</Alert>}

        <div>
          <p className="mb-2 text-sm font-medium text-slate-700">Logo</p>
          <div className="flex items-center gap-4">
            <div className="flex h-16 w-16 shrink-0 items-center justify-center overflow-hidden rounded-lg border border-slate-200 bg-slate-50">
              {logoUrl ? (
                <img src={logoUrl} alt={`${org.name} logo`} className="h-full w-full object-contain" />
              ) : (
                <span className="text-lg font-bold text-slate-300">{org.name.slice(0, 1).toUpperCase()}</span>
              )}
            </div>
            <div>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/png,image/jpeg,image/webp,image/svg+xml"
                className="hidden"
                onChange={handleFileChange}
              />
              <Button
                type="button"
                variant="secondary"
                size="sm"
                isLoading={logoMutation.isPending}
                onClick={() => fileInputRef.current?.click()}
              >
                <Upload className="h-3.5 w-3.5" /> Upload logo
              </Button>
              <p className="mt-1.5 text-xs text-slate-500">PNG, JPEG, WebP or SVG, up to 2MB.</p>
            </div>
          </div>
        </div>

        <div>
          <p className="mb-2 text-sm font-medium text-slate-700">Accent color</p>
          <div className="flex items-center gap-3">
            <input
              type="color"
              value={color}
              onChange={(e) => setColor(e.target.value)}
              className="h-9 w-9 cursor-pointer rounded-md border border-slate-300 p-0.5"
            />
            <span className="text-sm font-mono text-slate-600">{color}</span>
            <Button
              type="button"
              variant="secondary"
              size="sm"
              isLoading={colorMutation.isPending}
              onClick={() => colorMutation.mutate()}
              disabled={color === (org.brandColor ?? DEFAULT_COLOR)}
            >
              Save
            </Button>
          </div>
        </div>
      </CardBody>
    </Card>
  );
}

// Per-customer settings (Sites/Users' sibling tab). Branding is the first
// section; more sections (e.g. default charge type, billing cadence
// overrides) belong here as the customer detail page grows, each as its
// own titled block in this same flex column. Catalog categories moved to
// each site's own settings (see SiteCategoriesModal) - they're independent
// per site now, not shared across the whole customer.
export function CustomerSettingsTab({ org, sites }: { org: CustomerOrganisation; sites: CustomerSite[] }) {
  return (
    <div className="flex flex-col gap-6">
      <div>
        <h2 className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-500">Branding</h2>
        <BrandingSection org={org} />
      </div>

      <div>
        <h2 className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-500">Billing</h2>
        <BillingSection orgId={org.id} invoiceEmail={org.invoiceEmail} />
      </div>

      <div>
        <h2 className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-500">Contracts</h2>
        <AdHocContractsSection orgId={org.id} sites={sites} />
      </div>
    </div>
  );
}
